/* === IMPORTS === */
import { createReducer } from "@reduxjs/toolkit"

import { cleanSpace } from "actions/auth"
import { makeLib } from "utils/misc"
import {
  addMessage,
  GLOBAL_NOTIFICATIONS,
  MESSAGE_TYPE_ERROR,
  MESSAGE_TYPE_SUCCESS,
} from "utils/notifications"
import {
  createSpaceRequest,
  deleteSpaceRequest,
  getSpacesRequest,
  leaveSpaceRequest,
  updateSpaceRequest
} from "utils/spaces"
import {
  clearSpace,
  clearSpaceCategory,
  getSpace} from "utils/storage"

import { makeActions } from "./utiliducks"


/* == ACTIONS === */
const actionList = [
  "setSpacesAction",
  "addSpaceAction",
  "updateSpaceAction",
  "removeSpaceAction",
  "setPagingAction",
  "appendSpacesAction",
]
const {
  setSpacesAction,
  addSpaceAction,
  updateSpaceAction,
  removeSpaceAction,
  setPagingAction,
  appendSpacesAction,
} = makeActions("spaces", actionList)

/* === INITIAL STATE === */
const initialState = {
  spaces: [],
  spacesLib: {},
  paging: {
    previous_cursor: "",
    next_cursor: ""
  },
}

/* === Reducer === */
export default createReducer(initialState, {
  [setSpacesAction]: (state, { payload: { spaces }}={}) => ({
    ...state,
    spaces,
    spacesLib: makeLib({data: spaces})
  }),
  [appendSpacesAction]: (state, { payload: { spaces: newSpaces } } = {}) => {
    // Filter out spaces that already exist in the current state
    const existingSpaceIds = new Set(state.spaces.map(space => space.id))
    const uniqueNewSpaces = newSpaces.filter(space => !existingSpaceIds.has(space.id))

    // Combine existing spaces with only the unique new spaces
    const combinedSpaces = [...state.spaces, ...uniqueNewSpaces]
    return {
      ...state,
      spaces: combinedSpaces,
      spacesLib: makeLib({ data: combinedSpaces }),
    }
  },
  [addSpaceAction]: (state, { payload: { space }}={}) => ({
    ...state,
    spaces: [
      ...state.spaces,
      space
    ],
    spacesLib: {
      ...state.spacesLib,
      [space.id]: space
    }
  }),
  [updateSpaceAction]: (state, { payload: { space: updatedSpace }}={}) => {
    const { id } = updatedSpace
    return {
      ...state,
      spaces: state.spaces.map(space => space.id === id? updatedSpace : space),
      spacesLib: {
        ...state.spacesLib,
        [id]: {
          ...state.spacesLib[id], ...updatedSpace
        }
      }
    }
  },
  [removeSpaceAction]: (state, { payload: { id }}={}) => {
    const spaces = state.spaces.filter(space => id !== space.id)
    return {
      ...state,
      spaces,
      spacesLib: makeLib({data: spaces})
    }
  },
  [setPagingAction]: (state, { payload: { paging } }) => ({
    ...state,
    paging
  }),
})


/* === DISPATCHERS === */
/**
 * Fetches spaces from the server and updates the Redux store.
 *
 * @param {string} [cursor] - Optional pagination cursor for fetching the next set of spaces
 * @param {string} [limit] - Optional pagination limit to set the number of returning spaces (default number is 50)
 * @returns {Promise<Array>} A promise that resolves to the fetched spaces data
 * @throws {Error} If the spaces cannot be retrieved from the server
 *
 * @description
 * This function handles fetching spaces from the server and updating the Redux store.
 * If a cursor is provided, it appends the new spaces to the existing list.
 * If no cursor is provided, it replaces the entire list of spaces.
 * It also updates the pagination information in the store.
 *
 * Error handling:
 * - Logs errors to the console
 * - Displays an error notification to the user
 */
export const getSpaces = (cursor = "", limit = "50") => {
  return async dispatch => {
    try {
      const {data, paging } = await getSpacesRequest(cursor, limit)
      if (cursor) {
        dispatch(appendSpacesAction({spaces: data}))
      } else {
        dispatch(setSpacesAction({spaces: data}))
      }
      if (paging) dispatch(setPagingAction({paging}))
      return {data, paging}
    }
    catch(error) {
      console.error(`${error.name}: ${error.message}`)
      addMessage({
        target: GLOBAL_NOTIFICATIONS,
        text: "Spaces could not be retrieved",
        subtext: error.message,
        type: MESSAGE_TYPE_ERROR
      })
    }
  }
}

/**
 * Creates a new space and adds it to the Redux store.
 *
 * @param {Object} params - The parameters for creating a space
 * @param {string} params.id - The unique identifier for the space
 * @param {string} params.name - The display name of the space
 * @param {string} params.color - The color code for the space
 * @param {string} params.visibility - The visibility setting for the space
 * @returns {Promise<Object>} A promise that resolves to the created space object
 *
 * @description
 * This function creates a new space with the provided parameters and adds it to the Redux store.
 * It also displays a success notification to the user.
 *
 * The space settings include a menu configuration with visibility options.
 */
export const createSpace = ({id, name, color, visibility}) => {
  return async dispatch => {
    const body = {
      id,
      name,
      color,
      settings: [
        {
          name: "menu",
          options: {
            visibility
          }
        }
      ]
    }
    const space = await createSpaceRequest(body)
    dispatch(addSpaceAction({space}))

    addMessage({
      target: GLOBAL_NOTIFICATIONS,
      text: "Space Created",
      subtext: `${name} space was created successfully.`,
      type: MESSAGE_TYPE_SUCCESS,
      timeout: 4000
    })
    return space
  }
}

/**
 * Updates an existing space and refreshes it in the Redux store.
 *
 * @param {Object} params - The parameters for updating a space
 * @param {string} params.id - The unique identifier of the space to update
 * @param {string} params.name - The new display name for the space
 * @param {string} params.color - The new color code for the space
 * @param {boolean} params.allow_admin_access - Whether to allow admin access to the space
 * @param {string} params.visibility - The new visibility setting for the space
 * @returns {Promise<Object|undefined>} A promise that resolves to the updated space object if successful
 *
 * @description
 * This function updates an existing space with the provided parameters and refreshes it in the Redux store.
 * It also displays a success notification to the user.
 *
 * Error handling:
 * - Displays an error notification to the user if the update fails
 * - Returns undefined if the update fails
 */
export function updateSpace({
  id,
  name,
  color,
  allow_admin_access,
  visibility
}) {

  const body = {
    name,
    color,
    allow_admin_access,
    settings: [
      {
        name: "menu",
        options: {
          visibility
        }
      }
    ]
  }

  return async dispatch => {
    try {
      const space = await updateSpaceRequest(id, body)

      await dispatch(updateSpaceAction({space}))

      addMessage({
        target: GLOBAL_NOTIFICATIONS,
        text: "Space Updated",
        subtext: `${name} space was updated successfully.`,
        type: MESSAGE_TYPE_SUCCESS,
        timeout: 4000
      })

      return space

    } catch(error) {
      addMessage({
        target: GLOBAL_NOTIFICATIONS,
        text: "Space could not be updated",
        subtext: error.message,
        type: MESSAGE_TYPE_ERROR
      })
    }
  }
}

/**
 * Deletes a space and removes it from the Redux store.
 *
 * @param {string} id - The unique identifier of the space to delete
 * @returns {Promise<boolean>} A promise that resolves to true if deletion was successful, false otherwise
 *
 * @description
 * This function deletes a space from the server and removes it from the Redux store.
 * It also cleans up any related data in local storage and displays a success notification.
 *
 * Error handling:
 * - Logs errors to the console
 * - Displays an error notification to the user
 * - Returns false if the deletion fails
 */
export const deleteSpace = (id) => {
  return async dispatch => {
    try {
      await deleteSpaceRequest(id)
      await dispatch(cleanSpace())

      // Remove space related items from localstorage
      clearSpaceCategory(id)
      const savedSpace = getSpace()
      if (savedSpace === id) {
        clearSpace()
      }
      await dispatch(removeSpaceAction({id}))

      addMessage({
        target: GLOBAL_NOTIFICATIONS,
        text: "Space deleted successfully",
        type: MESSAGE_TYPE_SUCCESS,
        timeout: 4000
      })

      return true
    }
    catch(error) {
      console.error(`${error.name}: ${error.message}`)
      addMessage({
        target: GLOBAL_NOTIFICATIONS,
        text: "Space could not be deleted",
        subtext: error.message,
        type: MESSAGE_TYPE_ERROR
      })
      return false
    }
  }
}

/**
 * Allows a user to leave a space.
 *
 * @param {string} id - The unique identifier of the space to leave
 * @returns {Promise<boolean>} A promise that resolves to true if leaving was successful, false otherwise
 *
 * @description
 * This function allows a user to leave a space. It removes the space from local storage
 * and cleans up any related data. It does not remove the space from the Redux store.
 *
 * Error handling:
 * - Logs errors to the console
 * - Displays an error notification to the user
 * - Returns false if leaving the space fails
 */
export const leaveSpace = (id) => {
  return async dispatch => {
    try {
      await leaveSpaceRequest(id)
      dispatch(cleanSpace())

      // Remove space related items from localstorage
      clearSpaceCategory(id)
      const savedSpace = getSpace()
      if (savedSpace === id) {
        clearSpace()
      }

      return true
    }
    catch(error) {
      console.error(`${error.name}: ${error.message}`)
      addMessage({
        target: GLOBAL_NOTIFICATIONS,
        text: "Error leaving Space",
        subtext: error.message,
        type: MESSAGE_TYPE_ERROR
      })
      return false
    }
  }
}
